import { forwardRef, useImperativeHandle, useState } from "react";
import QuotationService from '../../services/QuotationService';
import PrintTemplateService from '../../services/PrintTemplateService';
import html2pdf from 'html2pdf.js';

const QuotationReceipt = forwardRef((props, ref) => {
    const [data, setData] = useState(null);
    const [template, setTemplate] = useState('');

    const isTemplateEmpty = (html) => {
        if (!html) return true;
        const trimmed = html.trim();
        return trimmed === "" || trimmed === "<p><br></p>" || trimmed === "<p></p>";
    };

    const fetchFullData = async (quotationId) => {
        const res = await QuotationService.get(quotationId);
        if (res.success && res.data) {
            const templateRes = await PrintTemplateService.getTemplate('quotation');
            let content = '';
            if (templateRes.success && templateRes.data && !isTemplateEmpty(templateRes.data.content)) {
                content = templateRes.data.content;
            } else {
                content = getFallbackTemplate();
            }
            return { quotationData: res.data, content };
        }
        throw new Error("Teklif verisi alınamadı");
    };

    const downloadPDF = async (quotationId) => {
        try {
            const { quotationData, content } = await fetchFullData(quotationId);
            const processedHtml = processTemplate(content, quotationData);

            const element = document.createElement('div');
            element.innerHTML = processedHtml;
            element.style.width = "190mm"; // Fits A4 printable width (210mm - 20mm margins)

            // Inject styles for Quill editor classes (alignment, size, indent) in PDF
            const style = document.createElement('style');
            style.innerHTML = `
                .ql-align-center { text-align: center !important; }
                .ql-align-right { text-align: right !important; }
                .ql-align-justify { text-align: justify !important; }
                .ql-indent-1 { padding-left: 3em !important; }
                .ql-indent-2 { padding-left: 6em !important; }
                .ql-size-small { font-size: 0.75em !important; }
                .ql-size-large { font-size: 1.5em !important; }
                .ql-size-huge { font-size: 2.5em !important; }
            `;
            element.appendChild(style);

            const opt = {
                margin: 10,
                filename: `Teklif_${quotationData.quotationNo || quotationId}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };

            await html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error("PDF indirme hatası:", error);
        }
    };

    const downloadWord = async (quotationId) => {
        try {
            const { quotationData, content } = await fetchFullData(quotationId);
            const processedHtml = processTemplate(content, quotationData);

            // Basic HTML to Word Blob conversion (browser compatible)
            const header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' "+
                           "xmlns:w='urn:schemas-microsoft-com:office:word' "+
                           "xmlns='http://www.w3.org/TR/REC-html40'>"+
                           "<head><meta charset='utf-8'><title>Teklif</title></head><body>";
            const footer = "</body></html>";
            const sourceHTML = header + processedHtml + footer;

            const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML);
            const fileLink = document.createElement("a");
            document.body.appendChild(fileLink);
            fileLink.href = source;
            fileLink.download = `Teklif_${quotationData.quotationNo || quotationId}.doc`;
            fileLink.click();
            document.body.removeChild(fileLink);
        } catch (error) {
            console.error("Word indirme hatası:", error);
        }
    };

    const print = async (quotationId) => {
        if (!quotationId) return;
        try {
            const { quotationData, content } = await fetchFullData(quotationId);
            setData(quotationData);
            setTemplate(processTemplate(content, quotationData));
            setTimeout(() => {
                window.print();
            }, 500);
        } catch (error) {
            console.error("Yazdırma sırasında hata:", error);
        }
    };

    const fmtMoney = (v) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(v ?? 0);
    const fmtDate = (isoOrDate) => (isoOrDate ? new Date(isoOrDate).toLocaleDateString("tr-TR") : "");

    const getFallbackTemplate = () => {
        return `<div style="font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #334155; line-height: 1.6; padding: 10px; background: #fff;">
            <!-- Top Accent Bar -->
            <div style="height: 6px; background: linear-gradient(90deg, #6366f1 0%, #06b6d4 100%); border-radius: 3px 3px 0 0; margin-bottom: 20px;"></div>
            
            <!-- Header Block -->
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 0 10px; margin-bottom: 30px;">
                <div>
                    <h1 style="margin: 0; font-size: 26pt; font-weight: 800; color: #1e293b; letter-spacing: -0.5px; text-transform: uppercase;">TEKLİF FORMU</h1>
                    <p style="margin: 4px 0 0 0; font-size: 10pt; color: #64748b; font-weight: 500;">Kasım Benim Muhasebe Sistemi</p>
                </div>
                <div style="text-align: right;">
                    <h2 style="margin: 0; font-size: 15pt; font-weight: 700; color: #6366f1; text-transform: uppercase;">{{FirmaAdi}}</h2>
                    <p style="margin: 2px 0 0 0; font-size: 8.5pt; color: #64748b;">{{FirmaTelefon}}</p>
                </div>
            </div>

            <!-- Metadata Panel -->
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center;">
                <div style="text-align: center; flex: 1; border-right: 1px solid #cbd5e1;">
                    <span style="font-size: 7.5pt; font-weight: 700; text-transform: uppercase; color: #94a3b8; display: block; margin-bottom: 4px;">Teklif Numarası</span>
                    <strong style="font-size: 11pt; color: #0f172a;">{{TeklifNo}}</strong>
                </div>
                <div style="text-align: center; flex: 1; border-right: 1px solid #cbd5e1;">
                    <span style="font-size: 7.5pt; font-weight: 700; text-transform: uppercase; color: #94a3b8; display: block; margin-bottom: 4px;">Teklif Tarihi</span>
                    <strong style="font-size: 11pt; color: #0f172a;">{{TeklifTarihi}}</strong>
                </div>
                <div style="text-align: center; flex: 1;">
                    <span style="font-size: 7.5pt; font-weight: 700; text-transform: uppercase; color: #94a3b8; display: block; margin-bottom: 4px;">Geçerlilik Tarihi</span>
                    <strong style="font-size: 11pt; color: #ef4444;">{{GecerlilikTarihi}}</strong>
                </div>
            </div>

            <!-- Info Block: Sender & Receiver -->
            <div style="display: flex; gap: 30px; margin-bottom: 35px;">
                <!-- Sender (Firma) -->
                <div style="flex: 1;">
                    <h3 style="margin: 0 0 10px 0; font-size: 9.5pt; font-weight: 800; color: #6366f1; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding-bottom: 6px;">Teklif Veren (Hizmet Sağlayıcı)</h3>
                    <div style="font-size: 10pt; font-weight: 700; color: #0f172a; margin-bottom: 6px;">{{FirmaAdi}}</div>
                    <div style="font-size: 9pt; color: #475569; line-height: 1.5; margin-bottom: 8px;">{{FirmaAdresi}}</div>
                    <div style="font-size: 9pt; color: #475569;"><span style="font-weight: 600; color: #334155;">Telefon:</span> {{FirmaTelefon}}</div>
                </div>
                
                <!-- Receiver (Müşteri) -->
                <div style="flex: 1;">
                    <h3 style="margin: 0 0 10px 0; font-size: 9.5pt; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding-bottom: 6px;">Teklif Edilen (Müşteri)</h3>
                    <div style="font-size: 10pt; font-weight: 700; color: #0f172a; margin-bottom: 6px;">{{MusteriAdi}}</div>
                    <div style="font-size: 9pt; color: #475569; line-height: 1.5; margin-bottom: 8px;">{{MusteriAdresi}}</div>
                    <div style="font-size: 9pt; color: #475569;"><span style="font-weight: 600; color: #334155;">Telefon:</span> {{MusteriTelefon}}</div>
                </div>
            </div>

            <!-- Items Section -->
            <div style="margin-bottom: 30px;">
                <h3 style="margin: 0 0 12px 0; font-size: 9.5pt; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.5px;">Ürün ve Hizmet Kalemleri</h3>
                {{UrunTablosu}}
            </div>

            <!-- Bottom Section: Notes & Totals -->
            <div style="display: flex; justify-content: space-between; gap: 30px; margin-bottom: 40px; align-items: flex-start;">
                <!-- Left: Notes -->
                <div style="flex: 1.2; background: #f8fafc; border-left: 4px solid #6366f1; padding: 15px; border-radius: 0 8px 8px 0; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0;">
                    <h4 style="margin: 0 0 8px 0; font-size: 9pt; font-weight: 700; color: #0f172a; text-transform: uppercase;">Teklif Notları & Koşullar</h4>
                    <div style="font-size: 8.5pt; color: #475569; line-height: 1.5; white-space: pre-wrap;">{{TeklifNotu}}</div>
                </div>
                
                <!-- Right: Totals Table -->
                <div style="flex: 0.8; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
                        <tr>
                            <td style="padding: 6px 0; color: #64748b;">Ara Toplam:</td>
                            <td style="padding: 6px 0; text-align: right; font-weight: 600; color: #1e293b;">{{AraToplam}}</td>
                        </tr>
                        <tr>
                            <td style="padding: 6px 0; color: #64748b; border-bottom: 1px solid #e2e8f0; padding-bottom: 10px;">KDV Toplam:</td>
                            <td style="padding: 6px 0; text-align: right; font-weight: 600; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 10px;">{{KdvToplam}}</td>
                        </tr>
                        <tr>
                            <td style="padding: 12px 0 0 0; font-weight: 700; color: #0f172a; font-size: 10pt;">GENEL TOPLAM:</td>
                            <td style="padding: 12px 0 0 0; text-align: right; font-weight: 800; color: #6366f1; font-size: 12pt;">{{GenelToplam}}</td>
                        </tr>
                    </table>
                </div>
            </div>

            <!-- Signatures -->
            <div style="margin-top: 50px; display: flex; justify-content: space-between; padding: 0 20px;">
                <div style="text-align: center; width: 220px;">
                    <p style="margin: 0 0 45px 0; font-size: 9pt; font-weight: 700; color: #64748b; text-transform: uppercase;">TEKLİF VEREN YETKİLİ</p>
                    <div style="border-top: 1px dashed #cbd5e1; padding-top: 8px; font-size: 9pt; font-weight: 600; color: #0f172a;">{{FirmaAdi}}</div>
                    <span style="font-size: 7.5pt; color: #94a3b8; display: block; margin-top: 2px;">İmza / Kaşe</span>
                </div>
                <div style="text-align: center; width: 220px;">
                    <p style="margin: 0 0 45px 0; font-size: 9pt; font-weight: 700; color: #64748b; text-transform: uppercase;">MÜŞTERİ ONAYI</p>
                    <div style="border-top: 1px dashed #cbd5e1; padding-top: 8px; font-size: 9pt; font-weight: 600; color: #0f172a;">{{MusteriAdi}}</div>
                    <span style="font-size: 7.5pt; color: #94a3b8; display: block; margin-top: 2px;">Tarih / İmza</span>
                </div>
            </div>
        </div>`;
    };

    const processTemplate = (html, quotationData) => {
        let result = html || "";
        
        // Clean up any HTML tags inside {{...}} placeholders that editor might have inserted
        result = result.replace(/\{\{[^}]*\}\}/g, (match) => match.replace(/<[^>]*>/g, ''));

        const replacements = {
            '{{FirmaAdi}}': quotationData.companyName || "---",
            '{{FirmaAdresi}}': quotationData.companyAddress || "---",
            '{{FirmaTelefon}}': quotationData.companyPhone || "---",
            '{{TeklifNo}}': quotationData.quotationNo || "---",
            '{{TeklifTarihi}}': fmtDate(quotationData.quotationDate),
            '{{GecerlilikTarihi}}': fmtDate(quotationData.validUntil),
            '{{MusteriAdi}}': quotationData.customerName || "---",
            '{{MusteriAdresi}}': quotationData.customerAddress || "---",
            '{{MusteriTelefon}}': quotationData.customerPhone || "---",
            '{{AraToplam}}': fmtMoney(quotationData.subTotal),
            '{{KdvToplam}}': fmtMoney(quotationData.vatTotal),
            '{{GenelToplam}}': fmtMoney(quotationData.grandTotal),
            '{{TeklifNotu}}': quotationData.note || "",
            '{{UrunTablosu}}': renderItemTable(quotationData)
        };

        Object.keys(replacements).forEach(key => {
            result = result.replace(new RegExp(key, 'g'), replacements[key]);
        });

        return result;
    };

    const renderItemTable = (quotationData) => {
        return `
            <table style="width: 100%; border-collapse: collapse; font-size: 9pt; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
                <thead>
                    <tr style="background: #1e293b; color: #ffffff; text-align: left;">
                        <th style="padding: 12px 14px; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px;">Ürün / Hizmet Tanımı</th>
                        <th style="padding: 12px 14px; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; text-align: center; width: 80px;">Miktar</th>
                        <th style="padding: 12px 14px; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; text-align: right; width: 120px;">Birim Fiyat</th>
                        <th style="padding: 12px 14px; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; text-align: center; width: 70px;">KDV</th>
                        <th style="padding: 12px 14px; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; text-align: right; width: 130px;">Toplam Tutar</th>
                    </tr>
                </thead>
                <tbody>
                    ${(quotationData.items || []).map((item, idx) => `
                        <tr style="background-color: ${idx % 2 === 0 ? '#ffffff' : '#f8fafc'}; border-bottom: 1px solid #f1f5f9;">
                            <td style="padding: 12px 14px; color: #1e293b; font-weight: 600; font-size: 9.5pt;">${item.productName || "---"}</td>
                            <td style="padding: 12px 14px; text-align: center; color: #475569; font-weight: 500;">${item.quantity || 0}</td>
                            <td style="padding: 12px 14px; text-align: right; color: #475569; font-weight: 500;">${fmtMoney(item.unitPrice)}</td>
                            <td style="padding: 12px 14px; text-align: center; color: #64748b; font-size: 8.5pt;">%${item.vatRate}</td>
                            <td style="padding: 12px 14px; text-align: right; color: #0f172a; font-weight: 700; font-size: 9.5pt;">${fmtMoney(item.lineTotal)}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        `;
    };

    useImperativeHandle(ref, () => ({
        print,
        downloadPDF,
        downloadWord
    }));

    if (!data) return null;

    return (
        <div className="print-only-quotation">
            <style type="text/css">
                {`
                .print-only-quotation { display: none; }
                @media print {
                    @page {
                        size: A4;
                        margin: 0 !important;
                    }

                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                        color: #000 !important;
                    }

                    .print-only-quotation {
                        display: block !important;
                        width: 100% !important;
                        margin: 0 !important;
                        background: white !important;
                        color: black !important;
                    }
                    .no-print { display: none !important; }

                    /* Quill alignment and size styles overrides for printing */
                    .ql-align-center { text-align: center !important; }
                    .ql-align-right { text-align: right !important; }
                    .ql-align-justify { text-align: justify !important; }
                    .ql-indent-1 { padding-left: 3em !important; }
                    .ql-indent-2 { padding-left: 6em !important; }
                    .ql-size-small { font-size: 0.75em !important; }
                    .ql-size-large { font-size: 1.5em !important; }
                    .ql-size-huge { font-size: 2.5em !important; }
                }
                `}
            </style>
            <div dangerouslySetInnerHTML={{ __html: template }} />
        </div>
    );
});

export default QuotationReceipt;
